import { getPinAlternateNames } from './pinAlternateNames';

const DEFAULT_PIN_NORMALIZED = {
  pin_role: 'unknown',
  capability_tags: [],
  voltage_min_v: null,
  voltage_max_v: null,
  current_limit_ma: null,
  is_5v_tolerant: null,
  electrical_type: 'unknown',
  restrictions: [],
  safe_usage_notes: [],
};

const DEFAULT_BOARD_NORMALIZED = {
  supply_voltage_min_v: null,
  supply_voltage_max_v: null,
  logic_voltage_min_v: null,
  logic_voltage_max_v: null,
  current_draw_typ_ma: null,
  current_draw_max_ma: null,
  rail_3v3_max_ma: null,
  rail_5v_max_ma: null,
  supported_protocols: [],
  analysis_confidence: 'low',
};

const PIN_ROLE_ALIASES = {
  gnd: 'ground',
  ground: 'ground',
  power: 'power-input-output',
  'power-input': 'power-input',
  'power-output': 'power-output',
  'power-input-output': 'power-input-output',
  gpio: 'gpio',
  digital: 'gpio',
  'digital i/o': 'gpio',
  analog: 'analog-input',
  'analog-input': 'analog-input',
  'analog input': 'analog-input',
  'analog-in': 'analog-input',
  adc: 'analog-input',
  ain: 'analog-input',
  'uart-rx': 'uart-rx',
  'uart-rx0': 'uart-rx',
  'uart receive': 'uart-rx',
  'uart-tx': 'uart-tx',
  'uart-tx0': 'uart-tx',
  'uart transmit': 'uart-tx',
  'i2c-sda': 'i2c-sda',
  sda: 'i2c-sda',
  'i2c-scl': 'i2c-scl',
  scl: 'i2c-scl',
  'spi-mosi': 'spi-mosi',
  mosi: 'spi-mosi',
  sdi: 'spi-mosi',
  'spi-miso': 'spi-miso',
  miso: 'spi-miso',
  sdo: 'spi-miso',
  'spi-sck': 'spi-sck',
  sck: 'spi-sck',
  sclk: 'spi-sck',
  clk: 'spi-sck',
  'spi-cs': 'spi-cs',
  cs: 'spi-cs',
  ss: 'spi-cs',
  ce: 'spi-cs',
  reset: 'reset',
  rst: 'reset',
  control: 'control',
  enable: 'enable',
  en: 'enable',
  boot: 'control',
  boot0: 'control',
  boot1: 'control',
  'boot-mode': 'control',
  'boot mode': 'control',
  strapping: 'control',
  strap: 'control',
  'chip-enable': 'enable',
  'chip enable': 'enable',
  'flash-data': 'flash-data',
  flash: 'flash-data',
  special: 'special',
};

const normalize_text = (value) => {
  return String(value || '').trim();
};

const normalize_key_text = (value) => {
  return normalize_text(value).toLowerCase();
};

export const parse_nullable_number = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
};

export const normalize_booleanish = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    if (value === 0) return false;
    if (value === 1) return true;
  }

  const normalized = normalize_key_text(value);

  if (['1', 'true', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'off'].includes(normalized)) {
    return false;
  }

  return null;
};

export const normalize_string_array = (value) => {
  if (value === null || value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((item) => normalize_text(item))
          .filter(Boolean)
      )
    );
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);

      if (Array.isArray(parsed)) {
        return normalize_string_array(parsed);
      }
    } catch {
      // CSV fallback below.
    }

    return normalize_string_array(trimmed.split(','));
  }

  return [];
};

const merge_unique_strings = (...collections) => {
  return Array.from(
    new Set(
      collections
        .flatMap((collection) => normalize_string_array(collection))
        .filter(Boolean)
    )
  );
};

const get_pin_name = (pin) => {
  return normalize_text(pin?.pin_name || pin?.pinName || pin?.name);
};

const get_pin_functions_text = (pin) => {
  return [
    pin?.functions,
    pin?.alternate_functions,
    pin?.alternateFunctions,
    pin?.description,
    pin?.pin_role,
    pin?.pinRole,
    pin?.group_name,
    pin?.groupName,
  ]
    .map((value) => normalize_text(value))
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const get_pin_alt_names = (pin) => {
  return getPinAlternateNames(pin);
};

const includes_any_name = (name_list, candidates) => {
  return candidates.some((candidate) => name_list.includes(candidate));
};

const build_pin_name_set = (pin) => {
  return [
    get_pin_name(pin),
    pin?.pin_number,
    pin?.pinNumber,
    pin?.svg_id,
    pin?.svgId,
    ...(get_pin_alt_names(pin)),
  ]
    .map((value) => normalize_key_text(value))
    .filter(Boolean);
};

export const normalize_pin_role_value = (value) => {
  const normalized = normalize_key_text(value);

  if (!normalized) {
    return '';
  }

  return PIN_ROLE_ALIASES[normalized] || normalized;
};

const infer_protocol_role_from_text = (pin, text) => {
  const pin_names = build_pin_name_set(pin);

  if (
    includes_any_name(pin_names, ['gnd', 'ground']) ||
    /\b(gnd|ground)\b/.test(text)
  ) {
    return { value: 'ground', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['3v3', '3.3v', '+3v3', 'v3']) ||
    /\b(3\.3v|3v3)\b/.test(text)
  ) {
    return { value: 'power-output', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['5v', '+5v', 'vu', 'usb']) ||
    /\b(5v|usb power|usb)\b/.test(text)
  ) {
    return { value: 'power-output', confidence: 'medium', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['vin', 'vraw', 'raw']) ||
    /\b(vin|raw|vraw)\b/.test(text)
  ) {
    return { value: 'power-input', confidence: 'medium', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['vcc', 'vdd']) ||
    /\b(vcc|vdd)\b/.test(text)
  ) {
    return { value: 'power-input', confidence: 'medium', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['rst', 'reset']) ||
    /\b(reset)\b/.test(text)
  ) {
    return { value: 'reset', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['en', 'enable', 'ch_pd', 'chip-enable']) ||
    /\b(enable|chip enable|ch_pd)\b/.test(text)
  ) {
    return { value: 'enable', confidence: 'medium', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['boot', 'boot0', 'boot1', 'strap', 'strapping']) ||
    /\bboot(?:\s*mode)?\b|\bstrapping\b/.test(text)
  ) {
    return { value: 'control', confidence: 'medium', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['tx', 'tx0', 'txd']) ||
    /\b(uart transmit|serial transmit|tx)\b/.test(text)
  ) {
    return { value: 'uart-tx', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['rx', 'rx0', 'rxd']) ||
    /\b(uart receive|serial receive|rx)\b/.test(text)
  ) {
    return { value: 'uart-rx', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['sda']) ||
    /\bsda\b/.test(text)
  ) {
    return { value: 'i2c-sda', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['scl']) ||
    /\bscl\b/.test(text)
  ) {
    return { value: 'i2c-scl', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['mosi', 'sdi']) ||
    /\b(mosi|sdi)\b/.test(text)
  ) {
    return { value: 'spi-mosi', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['miso', 'sdo']) ||
    /\b(miso|sdo)\b/.test(text)
  ) {
    return { value: 'spi-miso', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['sck', 'clk', 'sclk']) ||
    /\b(sck|sclk|clock|clk)\b/.test(text)
  ) {
    return { value: 'spi-sck', confidence: 'high', source: 'inferred' };
  }

  if (
    includes_any_name(pin_names, ['cs', 'ss', 'ce']) ||
    /\b(cs|chip select|slave select|ss)\b/.test(text)
  ) {
    return { value: 'spi-cs', confidence: 'high', source: 'inferred' };
  }

  if (includes_any_name(pin_names, ['s0', 's1', 's2', 's3', 'sc', 'sk'])) {
    return { value: 'flash-data', confidence: 'medium', source: 'inferred' };
  }

  if (
    /^\s*a\d+\s*$/i.test(get_pin_name(pin)) ||
    /\b(analog|adc)\b/.test(text)
  ) {
    return { value: 'analog-input', confidence: 'medium', source: 'inferred' };
  }

  if (
    /^\s*d\d+\s*$/i.test(get_pin_name(pin)) ||
    /^\s*gpio\d+\s*$/i.test(get_pin_name(pin)) ||
    /\b(gpio|digital)\b/.test(text)
  ) {
    return { value: 'gpio', confidence: 'medium', source: 'inferred' };
  }

  if (/\bpower\b/.test(text)) {
    return { value: 'power-input-output', confidence: 'low', source: 'inferred' };
  }

  return { value: 'unknown', confidence: 'low', source: 'default' };
};

export const infer_pin_role = (pin) => {
  return infer_protocol_role_from_text(pin, get_pin_functions_text(pin));
};

const get_existing_capability_tags = (pin) => {
  return normalize_string_array(pin?.capability_tags || pin?.capabilityTags)
    .map((tag) => normalize_pin_role_value(tag) || normalize_key_text(tag))
    .filter(Boolean);
};

export const infer_capability_tags = (pin) => {
  const text = get_pin_functions_text(pin);
  const role = normalize_pin_role_value(pin?.pin_role) || infer_pin_role(pin).value;
  const tags = new Set(get_existing_capability_tags(pin));

  if (role && role !== 'unknown') {
    tags.add(role);
  }

  if (role === 'ground') {
    tags.add('ground');
  }

  if (role.startsWith('power')) {
    tags.add('power');
  }

  if (role === 'gpio' || /\b(gpio|digital)\b/.test(text)) {
    tags.add('gpio');
  }

  if (/\bpio\b/.test(text)) {
    tags.add('pio');
  }

  if (role === 'analog-input' || /\b(analog|adc)\b/.test(text)) {
    tags.add('analog');
    tags.add('analog-input');
  }

  if (role.startsWith('uart-') || /\b(uart|serial)\b/.test(text)) {
    tags.add('uart');
  }

  if (role.startsWith('i2c-') || /\bi2c\b/.test(text)) {
    tags.add('i2c');
  }

  if (role.startsWith('spi-') || /\bspi\b/.test(text)) {
    tags.add('spi');
  }

  if (/\bpwm\b/.test(text)) {
    tags.add('pwm');
  }

  if (/\binterrupt\b/.test(text)) {
    tags.add('interrupt');
  }

  if (['reset', 'enable', 'control', 'flash-data', 'special'].includes(role)) {
    tags.add(role);
    if (role === 'reset' || role === 'enable' || role === 'control') {
      tags.add('control');
    }
    tags.add('special');
  }

  return Array.from(tags).filter(Boolean);
};

export const infer_pin_voltage = (pin, board = {}) => {
  const pin_names = build_pin_name_set(pin);
  const role = normalize_pin_role_value(pin?.pin_role) || infer_pin_role(pin).value;
  const tags = new Set(
    merge_unique_strings(pin?.capability_tags, infer_capability_tags(pin))
      .map((tag) => normalize_pin_role_value(tag) || normalize_key_text(tag))
  );

  if (includes_any_name(pin_names, ['gnd', 'ground'])) {
    return {
      min: 0,
      max: 0,
      confidence: 'high',
      source: 'inferred_from_pin_name',
    };
  }

  if (includes_any_name(pin_names, ['3v3', '3.3v', '+3v3', 'v3'])) {
    return {
      min: 3.3,
      max: 3.3,
      confidence: 'high',
      source: 'inferred_from_pin_name',
    };
  }

  if (includes_any_name(pin_names, ['5v', '+5v', 'vu', 'usb'])) {
    return {
      min: 5,
      max: 5,
      confidence: 'medium',
      source: 'inferred_from_pin_name',
    };
  }

  if (includes_any_name(pin_names, ['vin', 'raw', 'vraw', 'vcc', 'vdd'])) {
    return {
      min: null,
      max: null,
      confidence: 'low',
      source: 'unknown_input_range',
    };
  }

  const logic_min = parse_nullable_number(board?.logic_voltage_min_v);
  const logic_max = parse_nullable_number(board?.logic_voltage_max_v);

  if (
    role === 'gpio' ||
    role === 'analog-input' ||
    tags.has('gpio') ||
    tags.has('analog-input') ||
    tags.has('i2c-sda') ||
    tags.has('i2c-scl') ||
    tags.has('spi-mosi') ||
    tags.has('spi-miso') ||
    tags.has('spi-sck') ||
    tags.has('spi-cs') ||
    tags.has('uart-rx') ||
    tags.has('uart-tx')
  ) {
    if (logic_max !== null) {
      return {
        min: logic_min !== null ? logic_min : 0,
        max: logic_max,
        confidence: 'medium',
        source: 'inferred_from_board_logic_voltage',
      };
    }
  }

  return {
    min: null,
    max: null,
    confidence: 'low',
    source: 'default',
  };
};

export const infer_electrical_type = (pin) => {
  const role = normalize_pin_role_value(pin?.pin_role) || infer_pin_role(pin).value;
  const tags = new Set(infer_capability_tags(pin));

  if (role === 'ground') return 'ground';
  if (role.startsWith('power')) return 'power';
  if (role === 'analog-input' || tags.has('analog-input')) return 'analog_input';
  if (role === 'uart-tx' || tags.has('uart-tx')) return 'output';
  if (role === 'uart-rx' || tags.has('uart-rx')) return 'input';
  if (tags.has('i2c-sda') || tags.has('i2c-scl')) return 'open_drain';
  if (tags.has('gpio')) return 'bidirectional';
  if (['reset', 'enable', 'control', 'flash-data', 'special'].includes(role)) return 'special';

  return 'unknown';
};

export const infer_supported_protocols = (board, pins = []) => {
  const explicit_protocols = normalize_string_array(board?.supported_protocols)
    .map((protocol) => normalize_key_text(protocol))
    .map((protocol) => (protocol === 'adc' ? 'analog' : protocol))
    .filter(Boolean);
  const protocols = new Set(explicit_protocols);
  const all_tags = pins.flatMap((pin) => infer_capability_tags(pin));

  if (all_tags.includes('i2c') || (all_tags.includes('i2c-sda') && all_tags.includes('i2c-scl'))) {
    protocols.add('i2c');
  }

  if (
    all_tags.includes('spi') ||
    all_tags.includes('spi-mosi') ||
    all_tags.includes('spi-miso') ||
    all_tags.includes('spi-sck')
  ) {
    protocols.add('spi');
  }

  if (all_tags.includes('uart') || all_tags.includes('uart-rx') || all_tags.includes('uart-tx')) {
    protocols.add('uart');
  }

  return Array.from(protocols);
};

export {
  DEFAULT_PIN_NORMALIZED,
  DEFAULT_BOARD_NORMALIZED,
  merge_unique_strings,
};
